import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PersonnelProject, TMWorker } from "@/hooks/usePersonnelProjects";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  approved: { label: "Approved", className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  paid: { label: "Paid", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" },
  voided: { label: "Voided", className: "bg-red-100 text-red-700" },
};

interface Props {
  project: PersonnelProject;
  workers: TMWorker[];
}

export function PersonnelVendorBillsTab({ project, workers }: Props) {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Get vendor IDs from workers
  const vendorIds = workers.map((w) => w.vendor_id).filter(Boolean) as string[];

  const { data: bills = [], isLoading } = useQuery({
    queryKey: ["personnel_vendor_bills", project.id],
    queryFn: async () => {
      if (vendorIds.length === 0) return [];
      const { data, error } = await supabase
        .from("bills")
        .select("*, vendors(name), bill_lines(description, quantity, unit_price, line_total)")
        .eq("project_id", (project as any).project_id ?? project.id)
        .in("vendor_id", vendorIds)
        .order("bill_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user && vendorIds.length > 0,
  });

  const workerNameByVendor = new Map<string, string>();
  workers.forEach((w) => {
    if (w.vendor_id) workerNameByVendor.set(w.vendor_id, w.worker_name);
  });

  return (
    <div className="space-y-4 mt-4">
      <h2 className="text-base font-semibold">Vendor Bills ({bills.length})</h2>
      {isLoading ? (
        <p className="text-center py-8 text-muted-foreground">Loading…</p>
      ) : bills.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">
          No vendor bills generated yet. Approve a timesheet and click "Generate Invoice & Bills" to create them.
        </p>
      ) : isMobile ? (
        <div className="space-y-3">
          {bills.map((b: any) => {
            const badge = STATUS_BADGE[b.status] ?? STATUS_BADGE.draft;
            const workerName = b.vendor_id ? workerNameByVendor.get(b.vendor_id) ?? b.vendors?.name ?? "—" : "—";
            return (
              <Card key={b.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{b.bill_number}</span>
                    <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                  </div>
                  <p className="font-medium text-sm">{workerName}</p>
                  <p className="text-xs text-muted-foreground">{b.description || format(new Date(b.bill_date), "MMM d, yyyy")}</p>
                  <div className="text-lg font-bold">{fmt(Number(b.total ?? 0))}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bill #</TableHead>
                  <TableHead>Worker</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bills.map((b: any) => {
                  const badge = STATUS_BADGE[b.status] ?? STATUS_BADGE.draft;
                  const workerName = b.vendor_id ? workerNameByVendor.get(b.vendor_id) ?? b.vendors?.name ?? "—" : "—";
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-sm">{b.bill_number}</TableCell>
                      <TableCell className="font-medium">{workerName}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{b.description || format(new Date(b.bill_date), "MMM d, yyyy")}</TableCell>
                      <TableCell className="text-right font-semibold">{fmt(Number(b.total ?? 0))}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={badge.className}>{badge.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
